import { Box, Popover } from '@mui/material';
import React, { useState } from 'react';
import { HexColorPicker, RgbaColorPicker } from 'react-colorful';

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  enableAlpha?: boolean;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const parseRgbLikeToRgba = (
  color: string
): { r: number; g: number; b: number; a: number } | undefined => {
  const rgbMatch = color.match(
    /^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*((?:\d*\.\d+)|\d+))?\)$/i
  );

  if (!rgbMatch) {
    return undefined;
  }

  return {
    r: clamp(Number(rgbMatch[1]), 0, 255),
    g: clamp(Number(rgbMatch[2]), 0, 255),
    b: clamp(Number(rgbMatch[3]), 0, 255),
    a: clamp(
      Number.isFinite(Number(rgbMatch[4])) ? Number(rgbMatch[4]) : 1,
      0,
      1
    ),
  };
};

const parseColorToRgba = (
  color: string
): { r: number; g: number; b: number; a: number } => {
  const normalized = color.trim();

  const hexMatch = normalized.match(/^#([0-9a-fA-F]{6})$/);
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1].slice(0, 2), 16),
      g: parseInt(hexMatch[1].slice(2, 4), 16),
      b: parseInt(hexMatch[1].slice(4, 6), 16),
      a: 1,
    };
  }

  const parsedRgb = parseRgbLikeToRgba(normalized);
  if (parsedRgb) {
    return parsedRgb;
  }

  // Normalize hsl/hsla or named colors through the browser CSS parser.
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (context) {
      context.fillStyle = '#000000';
      context.fillStyle = normalized;

      const resolvedColor = context.fillStyle;

      const resolvedHex = resolvedColor.match(/^#([0-9a-fA-F]{6})$/);
      if (resolvedHex) {
        return {
          r: parseInt(resolvedHex[1].slice(0, 2), 16),
          g: parseInt(resolvedHex[1].slice(2, 4), 16),
          b: parseInt(resolvedHex[1].slice(4, 6), 16),
          a: 1,
        };
      }

      const resolvedRgb = parseRgbLikeToRgba(resolvedColor);
      if (resolvedRgb) {
        return resolvedRgb;
      }
    }
  }

  return { r: 0, g: 0, b: 0, a: 1 };
};

const toHex = (r: number, g: number, b: number): string => {
  const channelToHex = (value: number) =>
    clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');

  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
};

const toRgbaString = (rgba: { r: number; g: number; b: number; a: number }) =>
  `rgba(${Math.round(clamp(rgba.r, 0, 255))}, ${Math.round(
    clamp(rgba.g, 0, 255)
  )}, ${Math.round(clamp(rgba.b, 0, 255))}, ${clamp(rgba.a, 0, 1).toFixed(2)})`;

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  enableAlpha = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const rgbaColor = parseColorToRgba(value);
  const hexColor = toHex(rgbaColor.r, rgbaColor.g, rgbaColor.b);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(e.currentTarget)
        }
        sx={{
          width: 28,
          height: 28,
          borderRadius: '6px',
          backgroundColor: value,
          border: '1px solid rgba(0,0,0,0.18)',
          cursor: 'pointer',
          padding: 0,
          display: 'block',
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } },
        }}
      >
        <Box p={1}>
          {enableAlpha ? (
            <RgbaColorPicker
              color={rgbaColor}
              onChange={(next) => onChange(toRgbaString(next))}
            />
          ) : (
            <HexColorPicker color={hexColor} onChange={onChange} />
          )}
        </Box>
      </Popover>
    </>
  );
};
